// src/auth/AuthContext.tsx
import React, { useState, useEffect, createContext, useCallback } from "react";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { auth } from "@/shared/lib/firebase";
import { fetchAppUser } from "@/features/auth/api";
import type { AppUser, AuthContextType, AuthProviderProps } from "@/shared/types/auth";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingAppUser, setFetchingAppUser] = useState(false);

  const refetchAppUser = useCallback(async (): Promise<AppUser | null> => {
    if (!firebaseUser) return null;
    setFetchingAppUser(true);
    const data = await fetchAppUser(await firebaseUser.getIdToken());
    setAppUser(data);
    setFetchingAppUser(false);
    return data;
  }, [firebaseUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Clear appUser when firebaseUser is null (logout)
    if (!loading && !firebaseUser) {
      setAppUser(null);
      return;
    }

    // Don't auto-fetch user if on registration or login page
    const isAuthPage = window.location.pathname === '/register' || window.location.pathname === '/login' || window.location.pathname === '/auth/discord/callback';
    if (!loading && firebaseUser && !isAuthPage) {
      refetchAppUser();
    }
  }, [firebaseUser, loading, refetchAppUser]);

  return (
    <AuthContext.Provider value={{ firebaseUser, appUser, loading: loading || fetchingAppUser, refetchAppUser }}>
      {children}
    </AuthContext.Provider>
  );
}
